/**
 * What the platform owes, and what it has given up on.
 *
 * The outbox is the record of durable obligations: a domain mutation and the
 * intent to publish its event committed together, and the publisher drains it
 * afterwards. The numbers matter because the whole point is that somebody
 * notices before a consumer does.
 *
 * Redrive is offered separately from reading, and only to whoever holds it —
 * re-attempting something the platform already gave up on is an act, and it
 * never edits or deletes a prior delivery attempt.
 */

import { useCallback, useState } from "react";
import { api } from "../api/client";
import { NexusError } from "../api/errors";
import type {
  DeliveryAttempt,
  InvalidOutboxEntry,
  OutboxEntry,
  OutboxHealth,
  OutboxStatus,
  Page,
  PageProjection,
} from "../api/types";
import { usePagedCollection } from "../api/useCollection";
import { useResource } from "../api/useResource";
import {
  Banner,
  Card,
  Classification,
  CorrelationLink,
  Fact,
  Facts,
  Identifier,
  Mono,
  More,
  Pill,
  when,
  type Tone,
} from "../components/primitives";
import { Empty, Show } from "../components/states";
import { useSession } from "../session/SessionProvider";

/**
 * The page size all three of this screen's reads have always asked for.
 *
 * Unchanged on purpose. What changes is not how much is read at once but
 * whether the twenty-sixth row is reachable at all: it used to be absent
 * without the screen ever saying so, and a table that stops at the page
 * boundary in silence has told the reader the collection ended there.
 */
const PAGE = 25;

const STATUS_TONE: Record<OutboxStatus, Tone> = {
  pending: "wait",
  retrying: "busy",
  published: "ok",
  dead_lettered: "bad",
};

const ATTEMPT_TONE: Record<DeliveryAttempt["outcome"], Tone> = {
  succeeded: "ok",
  failed_transient: "wait",
  failed_permanent: "bad",
};

function Stat({
  label,
  value,
  tone,
  note,
}: {
  label: string;
  value: number;
  tone: Tone;
  note?: string | undefined;
}) {
  return (
    <div className="nx-card">
      <div className="nx-stat">
        <span className={`nx-stat__icon nx-pill--${tone}`} aria-hidden="true">
          <span className="nx-dot" />
        </span>
        <span>
          <span className="nx-stat__value">{value.toLocaleString()}</span>
          <span className="nx-stat__label" style={{ display: "block" }}>
            {label}
          </span>
          {note ? <span className="nx-stat__note">{note}</span> : null}
        </span>
      </div>
    </div>
  );
}

/** The obligations this deployment cannot read, and why. */
function Unreadable() {
  const readPage = useCallback(
    (cursor: string | null, signal: AbortSignal) =>
      api.get<Page<InvalidOutboxEntry>>("/core/events/outbox/invalid", {
        params: { limit: PAGE, cursor },
        signal,
      }),
    [],
  );
  const collection = usePagedCollection<InvalidOutboxEntry>(readPage);

  return (
    <Card title="Cannot be read" bodyless>
      <p className="nx-card__body" style={{ paddingBottom: 0, color: "var(--nx-text-soft)" }}>
        These obligations are stored exactly as they were written and do not satisfy their own
        contract, so the platform will not describe them or act on them. They are repaired where
        they were written — whatever wrote around the service.
      </p>
      <Show
        resource={collection.state}
        what="the unreadable obligations"
        onRetry={collection.refresh}
        empty={() =>
          collection.items.length === 0 ? (
            // This panel is on screen because the health count said the estate
            // is degraded; the list is read a moment later and can honestly
            // come back with none. A header over no rows says neither which
            // happened nor that anything happened at all.
            <Empty
              title="None to list."
              hint="The health count and this list were read at different moments. Either these have been repaired in between, or the count is about to change."
            />
          ) : null
        }
      >
        {() => (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <caption className="nx-visually-hidden">
                Durable obligations that fail their contract
              </caption>
              <thead>
                <tr>
                  <th scope="col">As stored</th>
                  <th scope="col">Claims</th>
                  <th scope="col">Why it cannot be read</th>
                  <th scope="col">Correlation</th>
                </tr>
              </thead>
              <tbody>
                {collection.items.map((row) => (
                  <tr key={row.event_id}>
                    <td>
                      <span className="nx-table__primary">{row.stored_event_type}</span>
                      <span className="nx-table__secondary" style={{ display: "block" }}>
                        {row.event_id}
                      </span>
                    </td>
                    <td>
                      <Pill tone="quiet">{row.stored_status.replace(/_/g, " ")}</Pill>
                      <Classification value={row.stored_classification} />
                    </td>
                    <td>{row.violation}</td>
                    <td>
                      {row.correlation_id ? (
                        <CorrelationLink value={row.correlation_id} />
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Show>
      <More
        collection={collection}
        label="Show more obligations"
        hint="This is one page of the obligations that cannot be read, not all of them."
        placement="bodyless"
      />
    </Card>
  );
}

function Attempts({ eventId }: { eventId: string }) {
  const readPage = useCallback(
    (cursor: string | null, signal: AbortSignal) =>
      api.get<Page<DeliveryAttempt>>(`/core/events/outbox/${eventId}/attempts`, {
        params: { limit: PAGE, cursor },
        signal,
      }),
    [eventId],
  );
  // The event is what the accumulated attempts belong to. Selecting another
  // event is a different history, not more of the last one's.
  const collection = usePagedCollection<DeliveryAttempt>(readPage, { resetKey: eventId });

  return (
    <>
      <Show
        resource={collection.state}
        what="the delivery attempts"
        onRetry={collection.refresh}
        empty={() =>
          collection.items.length === 0 ? (
            <p className="nx-hint">No attempt yet. The publisher has not reached this one.</p>
          ) : null
        }
      >
        {() => (
          <ul className="nx-timeline">
            {collection.items.map((attempt) => (
              <li key={attempt.attempt_id}>
                <span className={`nx-timeline__marker nx-pill--${ATTEMPT_TONE[attempt.outcome]}`}>
                  <span className="nx-dot" aria-hidden="true" />
                </span>
                <span>
                  <strong>Attempt {attempt.attempt_number}</strong>{" "}
                  <Pill tone={ATTEMPT_TONE[attempt.outcome]}>
                    {attempt.outcome.replace(/_/g, " ")}
                  </Pill>
                  <span className="nx-hint" style={{ display: "block" }}>
                    {when(attempt.started_at)}
                    {attempt.error_code ? ` · ${attempt.error_code}` : ""}
                  </span>
                  {attempt.detail ? <span className="nx-hint">{attempt.detail}</span> : null}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Show>
      <More
        collection={collection}
        label="Show more attempts"
        hint="This is one page of this event's delivery attempts, not all of them."
      />
    </>
  );
}

function EventDetail({ entry, onRedriven }: { entry: OutboxEntry; onRedriven: () => void }) {
  const session = useSession();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  return (
    <Card
      title={
        <span>
          {entry.envelope.event_type}
          <span className="nx-hint" style={{ display: "block", fontWeight: 400 }}>
            {entry.envelope.event_version}
          </span>
        </span>
      }
      actions={<Pill tone={STATUS_TONE[entry.status]}>{entry.status.replace(/_/g, " ")}</Pill>}
    >
      <Facts>
        <Fact label="Event ID">
          <Identifier value={entry.event_id} />
        </Fact>
        <Fact label="Classification">
          <Classification value={entry.envelope.data_classification} />
        </Fact>
        <Fact label="Source system">{entry.envelope.source_system}</Fact>
        <Fact label="Occurred">{when(entry.envelope.occurred_at)}</Fact>
        <Fact label="Enqueued">{when(entry.enqueued_at)}</Fact>
        <Fact label="Next attempt">{when(entry.next_attempt_at)}</Fact>
        <Fact label="Published">{when(entry.published_at)}</Fact>
        <Fact label="Attempts">{entry.attempts}</Fact>
        <Fact label="Correlation">
          <CorrelationLink value={entry.envelope.correlation_id} />
        </Fact>
        {entry.envelope.causation_id ? (
          <Fact label="Caused by">
            <CorrelationLink value={entry.envelope.causation_id} />
          </Fact>
        ) : null}
        {entry.last_error_code ? (
          <Fact label="Last error">
            <Mono>{entry.last_error_code}</Mono>
            {entry.last_error_detail ? (
              <span className="nx-hint" style={{ display: "block" }}>
                {entry.last_error_detail}
              </span>
            ) : null}
          </Fact>
        ) : null}
      </Facts>

      <p className="nx-hint" style={{ marginTop: 14 }}>
        The envelope carries no payload here — what is published is a reference and a hash, so an
        event never becomes a second copy of a record somebody else owns.
      </p>

      <h3 style={{ marginTop: 20, fontSize: "var(--nx-text-base)" }}>Delivery history</h3>
      <Attempts eventId={entry.event_id} />

      {session.may("outbox.redrive") ? (
        <div style={{ marginTop: 16 }}>
          <button
            type="button"
            className="nx-button nx-button--primary"
            disabled={busy || entry.status === "published"}
            onClick={() => {
              setBusy(true);
              setNote(null);
              void api
                .post(`/core/events/outbox/${entry.event_id}/redrive`, {}, { idempotent: true })
                .then(() => {
                  setNote("Queued again. The prior attempts stay on the record.");
                  onRedriven();
                })
                .catch((error: unknown) => {
                  // What the projection said a moment ago is not what decides
                  // this. The route decided again, and if a grant went away in
                  // between, the honest thing to show is the refusal.
                  setNote(
                    error instanceof NexusError && error.isForbidden
                      ? "Refused. Redrive is not permitted for you — the button was offered on a policy that has since changed."
                      : error instanceof Error
                        ? error.message
                        : "The redrive did not happen.",
                  );
                })
                .finally(() => {
                  setBusy(false);
                });
            }}
          >
            Redrive
          </button>
          <p className="nx-hint" style={{ marginTop: 8 }}>
            A redrive changes delivery state. It never edits or deletes a prior attempt.
          </p>
          {note ? (
            <p role="status" style={{ marginTop: 4 }}>
              {note}
            </p>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

export function EventBackbone() {
  const session = useSession();
  const [status, setStatus] = useState<"" | OutboxStatus>("");
  const [selected, setSelected] = useState<OutboxEntry | null>(null);

  const readHealth = useCallback(
    (signal: AbortSignal) => api.get<OutboxHealth>("/core/events/outbox/health", { signal }),
    [],
  );
  const health = useResource<OutboxHealth>(readHealth);

  /**
   * What the deployment cannot read, kept where paging cannot lose it.
   *
   * `usePagedCollection` reads pages as `PageOf`, which is narrower than the
   * `Page` this endpoint answers with: it does not carry `projection`. The
   * outbox is the one collection in the platform that fills that field in, and
   * dropping it here would take away the only disclosure this screen makes
   * about obligations the platform could not parse. So it is taken at the
   * boundary where the type is still `Page`, rather than reached for later
   * through a cast the type system cannot check.
   *
   * The latest answer wins, and nothing is added up. `unprojectable` is
   * documented as a count for the deployment and is computed over the tenant's
   * whole estate rather than over the window, so every page repeats the same
   * number — summing them would report one problem twice for having read two
   * pages. It is also unaffected by the status filter, which is why switching
   * filters does not make the estate look briefly healthy.
   */
  const [projection, setProjection] = useState<PageProjection | null>(null);

  const readEvents = useCallback(
    async (cursor: string | null, signal: AbortSignal): Promise<Page<OutboxEntry>> => {
      const answer = await api.get<Page<OutboxEntry>>("/core/events/outbox", {
        params: { limit: PAGE, cursor, ...(status ? { status } : {}) },
        signal,
      });
      // Only a request that was not superseded gets here: a request the session
      // aborted rejects at the await above, so a late answer cannot overwrite
      // the one in hand.
      setProjection(answer.projection ?? null);
      return answer;
    },
    [status],
  );
  // The status is what the accumulated pages belong to. A different filter is a
  // different collection, not a continuation of the last one.
  const events = usePagedCollection<OutboxEntry>(readEvents, { resetKey: status });

  const reloadBoth = useCallback(() => {
    health.reload();
    events.refresh();
  }, [health, events]);

  return (
    <>
      <Show resource={health.state} what="the backbone health" onRetry={health.reload}>
        {(numbers) => (
          <>
            {numbers.unprojectable > 0 ? (
              <Banner
                tone="bad"
                title={`${String(numbers.unprojectable)} obligation${
                  numbers.unprojectable === 1 ? "" : "s"
                } in this deployment cannot be read.`}
              >
                They are still here and nothing has been done to them. Something wrote around the
                service, and the list below is not the whole estate until they are repaired.
              </Banner>
            ) : null}
            {numbers.transport_enabled ? null : (
              <Banner tone="wait" title="External transport is not enabled in this deployment.">
                Events are still persisted durably and stay owed — the outbox is what makes the
                commit safe. Nothing is being delivered outward until a broker is configured.
              </Banner>
            )}
            <div className="nx-grid" style={{ marginBottom: 18 }}>
              <Stat
                label="Pending"
                value={numbers.pending}
                tone="wait"
                note={
                  numbers.oldest_pending_age_seconds === null
                    ? undefined
                    : `Oldest ${Math.round(numbers.oldest_pending_age_seconds)}s`
                }
              />
              <Stat label="Retrying" value={numbers.retrying} tone="busy" />
              <Stat
                label="Dead lettered"
                value={numbers.dead_lettered}
                tone="bad"
                note={numbers.dead_lettered > 0 ? "Requires intervention" : undefined}
              />
            </div>
          </>
        )}
      </Show>

      <div className="nx-split">
        <Card
          title="Outbox"
          actions={
            <span style={{ display: "flex", gap: 8 }}>
              <label className="nx-visually-hidden" htmlFor="outbox-status">
                Filter by status
              </label>
              <select
                id="outbox-status"
                className="nx-select"
                value={status}
                onChange={(event) => {
                  setStatus(event.target.value as "" | OutboxStatus);
                  setSelected(null);
                }}
              >
                <option value="">All statuses</option>
                <option value="pending">Pending</option>
                <option value="retrying">Retrying</option>
                <option value="published">Published</option>
                <option value="dead_lettered">Dead lettered</option>
              </select>
              <button type="button" className="nx-button" onClick={reloadBoth}>
                Refresh
              </button>
            </span>
          }
          bodyless
        >
          {/* Said while there is a list for it to be about. The deployment is
              degraded either way, and the health banner above says so
              regardless — but "they are listed below" is only true where there
              is a below, so a read that failed outright drops the sentence
              rather than pointing at nothing. A later page loading is not that
              case: the rows already read are still on screen. */}
          {projection?.status === "degraded" &&
          (events.state.kind === "ready" || events.items.length > 0) ? (
            <p
              className="nx-card__body"
              role="status"
              style={{ color: "var(--nx-bad)", paddingBottom: 0 }}
            >
              This list is short of {projection.unprojectable}{" "}
              {projection.unprojectable === 1 ? "obligation" : "obligations"} the platform cannot
              read.{" "}
              {events.items.length > 0
                ? "They are listed below rather than hidden."
                : "Nothing that has been read could be projected at all, so this list is empty rather than complete."}
            </p>
          ) : null}
          <Show
            resource={events.state}
            what="the outbox"
            onRetry={events.refresh}
            empty={() =>
              events.items.length === 0 ? (
                /*
                 * No rows is not the same fact as nothing owed, and on this
                 * endpoint alone the difference is reachable. `entries` is the
                 * one page in the platform not built by `Page.of`: it takes
                 * `has_more` from the rows the query returned and `items` from
                 * the ones that survived projection, so a window in which every
                 * row failed its own contract comes back empty and saying there
                 * is more. Everywhere else `Page.of` sets both from the same
                 * rows, which is why the unguarded reading is safe there and
                 * only here.
                 *
                 * So the two things that would have to be true before an empty
                 * table means an empty backlog are checked: that the walk
                 * finished, and that the collection did not say it was short.
                 */
                <Empty
                  title={
                    !events.hasMore && projection?.status !== "degraded"
                      ? "Nothing owed."
                      : "Nothing to show."
                  }
                  hint={
                    !events.hasMore && projection?.status !== "degraded"
                      ? "Every event this deployment accepted has been published."
                      : "Not the same as nothing being owed: what has been read either failed its own contract or has not reached the end of the backlog."
                  }
                />
              ) : null
            }
          >
            {() => (
              <div className="nx-table-wrap">
                <table className="nx-table">
                  <caption className="nx-visually-hidden">Events the platform still owes</caption>
                  <thead>
                    <tr>
                      <th scope="col">Event</th>
                      <th scope="col">Classification</th>
                      <th scope="col">Status</th>
                      <th scope="col">Attempts</th>
                      <th scope="col">Next / published</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.items.map((entry) => (
                      <tr
                        key={entry.event_id}
                        aria-selected={selected?.event_id === entry.event_id}
                      >
                        <td>
                          <button
                            type="button"
                            className="nx-rowbutton"
                            onClick={() => {
                              setSelected(entry);
                            }}
                          >
                            <span className="nx-table__primary">{entry.envelope.event_type}</span>
                            <span className="nx-table__secondary" style={{ display: "block" }}>
                              {entry.event_id}
                            </span>
                          </button>
                        </td>
                        <td>
                          <Classification value={entry.envelope.data_classification} />
                        </td>
                        <td>
                          <Pill tone={STATUS_TONE[entry.status]}>
                            {entry.status.replace(/_/g, " ")}
                          </Pill>
                        </td>
                        <td>{entry.attempts}</td>
                        <td>{when(entry.published_at ?? entry.next_attempt_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Show>
          <More
            collection={events}
            label="Show more events"
            hint="This is one page of what the backbone owes, not all of it."
            placement="bodyless"
          />
        </Card>

        {selected ? (
          <EventDetail entry={selected} onRedriven={reloadBoth} />
        ) : (
          <Card title="Select an event">
            <p className="nx-hint">
              Choose an event to see its envelope and every delivery attempt made for it.
            </p>
          </Card>
        )}
      </div>

      {health.state.kind === "ready" && health.state.value.unprojectable > 0 ? (
        <div style={{ marginTop: 18 }}>
          {session.may("outbox.read_unprojectable") ? (
            <Unreadable />
          ) : (
            <Card title="Cannot be read">
              <p className="nx-hint">
                Seeing which obligations cannot be read is granted apart from reading the backlog.
                The platform cannot vouch for what is in a row it could not parse, so being allowed
                to see the estate is not being allowed to see what it failed to understand.
              </p>
            </Card>
          )}
        </div>
      ) : null}
    </>
  );
}
